/**
 * ImageUpload component.
 *
 * Upload flow:
 * 1. User picks a file (or takes a photo on mobile via `capture="environment"`).
 * 2. The component sends the file to POST /api/upload (our Bun backend).
 * 3. The backend forwards it to Zipline using the server-side ZIPLINE_TOKEN.
 * 4. The backend returns the public Zipline URL.
 * 5. The parent receives the URL via onUpload callback.
 *
 * The ZIPLINE_TOKEN never leaves the server — no token is exposed in the browser.
 */
import { useRef, useState } from 'react';
import { Text, Loader, Box, Modal, Image } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  uploadDropzone,
  uploadPreviewGrid,
  uploadPreviewItem,
  uploadPreviewImg,
  uploadPreviewDelete,
  uploadPreviewClickable,
} from '../styles/theme.css';
import type { ItemImage } from '../types';
import { getToken, clearToken } from '../api/client';

interface ImageUploadProps {
  existingImages?: ItemImage[];
  onUpload: (ziplineUrl: string, ziplineId: string | undefined) => void;
  onDelete?: (imageId: number) => void;
}

async function uploadViaProxy(file: File): Promise<{ url: string; ziplineId: string | undefined }> {
  const form = new FormData();
  form.append('file', file);

  const token = getToken();
  if (token === null) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (res.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((data as { error: string }).error ?? `Upload failed: ${res.status}`);
  }

  const data: { url: string; ziplineId?: string } = await res.json();
  if (!data.url) throw new Error('Server did not return an image URL');
  return { url: data.url, ziplineId: data.ziplineId };
}

export function ImageUpload({ existingImages = [], onUpload, onDelete }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const { url, ziplineId } = await uploadViaProxy(file);
        onUpload(url, ziplineId);
      }
      notifications.show({ message: 'Billede(r) uploadet', color: 'green' });
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Upload fejlede',
        color: 'red',
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <div className={uploadDropzone} onClick={() => inputRef.current?.click()}>
        {uploading ? (
          <Loader size="sm" />
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Tryk for at tage billede eller vælge fil
            </Text>
            {/* <Text size="xs" c="dimmed" mt={4}>
              Billeder gemmes i Zipline
            </Text> */}
          </>
        )}
      </div>

      {existingImages.length > 0 && (
        <div className={uploadPreviewGrid}>
          {existingImages.map((img) => (
            <div key={img.id} className={`${uploadPreviewItem} ${uploadPreviewClickable}`}>
              <img
                src={img.zipline_url}
                alt="Billede af genstand"
                className={uploadPreviewImg}
                onClick={() => setLightboxUrl(img.zipline_url)}
              />
              {onDelete && (
                <button
                  className={uploadPreviewDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(img.id);
                  }}
                  title="Slet billede"
                  type="button"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Modal
        opened={lightboxUrl !== null}
        onClose={() => setLightboxUrl(null)}
        size="xl"
        padding={0}
        withCloseButton={false}
        styles={{ body: { padding: 0 } }}
        onClick={() => setLightboxUrl(null)}
      >
        {lightboxUrl && (
          <Image
            src={lightboxUrl}
            alt="Forstørret billede"
            fit="contain"
            mah="90vh"
            style={{ display: 'block', margin: '0 auto' }}
          />
        )}
      </Modal>
    </Box>
  );
}
